
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getSocket } from '../ChatPage/socket';

const CAMERA_MOVE_INTERVAL_MS = 50;

export const resolveAdminRealtimePageKey = () => {
    if (typeof window === 'undefined') return '';
    const pathname = String(window.location.pathname || '');

    if (pathname.startsWith('/admin/catalog-review')) return 'catalog-review';
    if (pathname.startsWith('/containerProductPage/product-seo-price')) return 'product-seo-price';

    return '';
};

const safeKeyPart = (value, fallback = 'state') => {
    const text = String(value ?? '')
        .trim()
        .replace(/[^a-z0-9:._|@/\-]+/gi, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 150);
    return text || fallback;
};

const semanticTargetId = (namespace, channelKey, field) => (
    `semantic:${safeKeyPart(namespace)}:${safeKeyPart(channelKey)}:${safeKeyPart(field)}`
).slice(0, 230);

const readStateField = (event, namespace, channelKey) => {
    if (event?.type !== 'state') return null;
    if (String(event?.state?.namespace || '') !== String(namespace || '')) return null;

    const prefix = `${safeKeyPart(channelKey)}.`;
    const stateKey = String(event?.state?.stateKey || '');
    if (!stateKey.startsWith(prefix)) return null;

    const field = stateKey.slice(prefix.length);
    if (!field) return null;

    return {
        field,
        value: event?.state?.value,
        revision: Number(event?.stateRevision || 0),
    };
};

/*
 * Явный semantic-channel: ничего не наблюдает автоматически и никогда не
 * реплеит DOM. Компонент сам вызывает publishField/publishPatch только там,
 * где у него действительно изменился принадлежащий ему state.
 *
 * Каждое поле хранится отдельным snapshot-key, поэтому параллельные изменения
 * разных полей не затирают друг друга целым объектом.
 */
export const useAdminRealtimeSemanticFields = ({
    namespace,
    channelKey,
    enabled = true,
} = {}) => {
    const [remoteState, setRemoteState] = useState({});
    const revisionsRef = useRef(new Map());
    const sequenceRef = useRef(0);
    const pendingSeedsRef = useRef(new Map());

    const pageKey = resolveAdminRealtimePageKey();
    const safeNamespace = safeKeyPart(namespace, 'admin');
    const safeChannelKey = safeKeyPart(channelKey, 'default');

    useEffect(() => {
        revisionsRef.current.clear();
        pendingSeedsRef.current.clear();
        sequenceRef.current = 0;
        setRemoteState({});
    }, [enabled, pageKey, safeChannelKey, safeNamespace]);

    const applyEvent = useCallback((event = {}) => {
        if (!enabled || !pageKey) return;
        if (String(event.pageKey || '') !== pageKey) return;

        const parsed = readStateField(event, safeNamespace, safeChannelKey);
        if (!parsed) return;

        const previousRevision = Number(revisionsRef.current.get(parsed.field) || 0);
        if (parsed.revision && parsed.revision < previousRevision) return;
        if (parsed.revision) revisionsRef.current.set(parsed.field, parsed.revision);

        setRemoteState((previous) => {
            if (Object.is(previous?.[parsed.field], parsed.value)) return previous;
            return {
                ...previous,
                [parsed.field]: parsed.value,
            };
        });
    }, [enabled, pageKey, safeChannelKey, safeNamespace]);

    const emitSemanticField = useCallback((field, value, { seedOnly = false, onAck } = {}) => {
        if (!enabled || !pageKey || !field) return false;
        const socket = getSocket();
        if (!socket.connected) return false;

        sequenceRef.current += 1;
        const safeField = safeKeyPart(field, 'field');

        socket.emit('adminRealtime:state', {
            pageKey,
            type: 'state',
            targetId: semanticTargetId(safeNamespace, safeChannelKey, safeField),
            sequence: sequenceRef.current,
            state: {
                namespace: safeNamespace,
                stateKey: `${safeChannelKey}.${safeField}`,
                value,
                seedOnly,
            },
        }, (response = {}) => {
            if (typeof onAck === 'function') onAck(response);
        });
        return true;
    }, [enabled, pageKey, safeChannelKey, safeNamespace]);

    const flushPendingSeeds = useCallback(() => {
        if (!enabled || !pageKey || !pendingSeedsRef.current.size) return;

        pendingSeedsRef.current.forEach((value, field) => {
            emitSemanticField(field, value, {
                seedOnly: true,
                onAck: (response = {}) => {
                    if (response?.ok) pendingSeedsRef.current.delete(field);
                },
            });
        });
    }, [emitSemanticField, enabled, pageKey]);

    useEffect(() => {
        if (!enabled || !pageKey) return undefined;

        const socket = getSocket();
        const onState = (event = {}) => {
            applyEvent(event);
            const parsed = readStateField(event, safeNamespace, safeChannelKey);
            if (parsed?.field) pendingSeedsRef.current.delete(parsed.field);
        };
        const onSnapshot = (snapshot = {}) => {
            if (String(snapshot.pageKey || '') !== pageKey) return;
            (Array.isArray(snapshot.states) ? snapshot.states : []).forEach(onState);
            // join/resync уже подтверждает, что realtime-комната готова. Теперь
            // можно безопасно создать только отсутствующие canonical defaults.
            flushPendingSeeds();
        };
        const onConnect = () => {
            window.setTimeout(flushPendingSeeds, 0);
        };

        socket.on('adminRealtime:state', onState);
        socket.on('adminRealtime:snapshot', onSnapshot);
        socket.on('connect', onConnect);

        // Если hook смонтировался после join, попросим текущий canonical snapshot.
        if (socket.connected) {
            socket.emit('adminRealtime:resync', { pageKey }, () => {});
        }

        return () => {
            socket.off('adminRealtime:state', onState);
            socket.off('adminRealtime:snapshot', onSnapshot);
            socket.off('connect', onConnect);
        };
    }, [applyEvent, enabled, flushPendingSeeds, pageKey, safeChannelKey, safeNamespace]);

    const publishField = useCallback((field, value) => (
        emitSemanticField(field, value)
    ), [emitSemanticField]);

    const publishPatch = useCallback((patch = {}) => {
        if (!patch || typeof patch !== 'object' || Array.isArray(patch)) return;
        Object.entries(patch).forEach(([field, value]) => publishField(field, value));
    }, [publishField]);

    const seedField = useCallback((field, value) => {
        if (!enabled || !pageKey || !field) return false;
        const safeField = safeKeyPart(field, 'field');
        pendingSeedsRef.current.set(safeField, value);

        return emitSemanticField(safeField, value, {
            seedOnly: true,
            onAck: (response = {}) => {
                if (response?.ok) pendingSeedsRef.current.delete(safeField);
            },
        });
    }, [emitSemanticField, enabled, pageKey]);

    const seedPatch = useCallback((patch = {}) => {
        if (!patch || typeof patch !== 'object' || Array.isArray(patch)) return;
        Object.entries(patch).forEach(([field, value]) => seedField(field, value));
    }, [seedField]);

    return useMemo(() => ({
        pageKey,
        remoteState,
        publishField,
        publishPatch,
        seedField,
        seedPatch,
    }), [pageKey, publishField, publishPatch, remoteState, seedField, seedPatch]);
};

const vectorObject = (value) => {
    if (Array.isArray(value)) {
        return {
            x: Number(value[0] || 0),
            y: Number(value[1] || 0),
            z: Number(value[2] || 0),
        };
    }

    return {
        x: Number(value?.x || 0),
        y: Number(value?.y || 0),
        z: Number(value?.z || 0),
    };
};

const vectorArray = (value) => {
    if (Array.isArray(value)) return value.slice(0, 3).map((item) => Number(item || 0));
    return [
        Number(value?.x || 0),
        Number(value?.y || 0),
        Number(value?.z || 0),
    ];
};

export const useAdminRealtimeCamera = ({
    viewportKey,
    enabled = true,
} = {}) => {
    const [remoteState, setRemoteState] = useState(null);
    const sequenceRef = useRef(0);
    const lastMoveAtRef = useRef(0);
    const lastRemoteRevisionRef = useRef(0);

    const pageKey = resolveAdminRealtimePageKey();
    const safeViewportKey = safeKeyPart(viewportKey, 'admin-3d');

    useEffect(() => {
        setRemoteState(null);
        lastRemoteRevisionRef.current = 0;
        sequenceRef.current = 0;
        lastMoveAtRef.current = 0;
    }, [enabled, pageKey, safeViewportKey]);

    const applyCameraEvent = useCallback((event = {}) => {
        if (!enabled || !pageKey) return;
        if (String(event.pageKey || '') !== pageKey) return;
        if (event.type !== 'camera') return;
        if (String(event.viewportKey || event.targetId || '') !== safeViewportKey) return;

        const socket = getSocket();
        if (String(event.socketId || '') === String(socket.id || '')) return;

        const revision = Number(event.stateRevision || 0);
        if (revision && revision < lastRemoteRevisionRef.current) return;
        if (revision) lastRemoteRevisionRef.current = revision;

        setRemoteState({
            viewportKey: safeViewportKey,
            position: vectorObject(event.position),
            target: vectorObject(event.target),
            quaternion: Array.isArray(event.quaternion) ? event.quaternion.slice(0, 4) : null,
            zoom: Number(event.zoom || 1),
            fov: Number(event.fov || 35),
            final: event.final === true,
            sequence: Math.max(
                Number(event.sequence || 0),
                revision,
                Date.now()
            ),
        });
    }, [enabled, pageKey, safeViewportKey]);

    useEffect(() => {
        if (!enabled || !pageKey || !safeViewportKey) return undefined;
        const socket = getSocket();

        const onState = (event = {}) => applyCameraEvent(event);
        const onSnapshot = (snapshot = {}) => {
            if (String(snapshot.pageKey || '') !== pageKey) return;
            (Array.isArray(snapshot.states) ? snapshot.states : []).forEach(applyCameraEvent);
        };

        socket.on('adminRealtime:state', onState);
        socket.on('adminRealtime:snapshot', onSnapshot);
        if (socket.connected) socket.emit('adminRealtime:resync', { pageKey }, () => {});

        return () => {
            socket.off('adminRealtime:state', onState);
            socket.off('adminRealtime:snapshot', onSnapshot);
        };
    }, [applyCameraEvent, enabled, pageKey, safeViewportKey]);

    const onChange = useCallback((state = {}) => {
        if (!enabled || !pageKey || !safeViewportKey) return;
        if (state.clear === true) return;

        const timestamp = Date.now();
        const final = state.interacting !== true;
        if (!final && timestamp - lastMoveAtRef.current < CAMERA_MOVE_INTERVAL_MS) return;
        if (!final) lastMoveAtRef.current = timestamp;

        const socket = getSocket();
        if (!socket.connected) return;

        sequenceRef.current += 1;
        const payload = {
            pageKey,
            type: 'camera',
            targetId: safeViewportKey,
            viewportKey: safeViewportKey,
            sequence: sequenceRef.current,
            position: vectorArray(state.position),
            target: vectorArray(state.target),
            quaternion: Array.isArray(state.quaternion) ? state.quaternion.slice(0, 4) : undefined,
            zoom: Number(state.zoom || 1),
            fov: Number(state.fov || 35),
            final,
        };

        if (!final && socket.volatile) socket.volatile.emit('adminRealtime:state', payload);
        else socket.emit('adminRealtime:state', payload);
    }, [enabled, pageKey, safeViewportKey]);

    return useMemo(() => ({
        viewportKey: safeViewportKey,
        remoteState,
        onChange,
    }), [onChange, remoteState, safeViewportKey]);
};
